<script setup lang="ts">
import type { DynamicAuctionItemDetails } from '@autobid/strapi-integration/rest/components'
import type { AuctionCarPagination, DetailsCarItemData } from '@autobid/ui/types/Car'
import type { DetailsCarItemDetails } from '@autobid/ui/types/components/CarDetails'
import CurrentAuctionButton from '@autobid/ui/components/elements/car/CurrentAuctionButton.vue'
import { useSpecialConditionNotification } from '@autobid/ui/composables/auction/useSpecialConditionNotification'
import { useAuction } from '@autobid/ui/composables/useAuction'
import { useAuctionPagesLink } from '@autobid/ui/composables/useAuctionPagesLink'
import { useCarSwipe } from '@autobid/ui/composables/useCarSwipe'
import { computed } from 'vue'

const props = defineProps<
  DynamicAuctionItemDetails & {
    car: DetailsCarItemData
    details: DetailsCarItemDetails
    auctionCarPagination: AuctionCarPagination
  }
>()

const { auction } = useAuction(props.car.auctionId)
const { getLink } = useAuctionPagesLink(props.car.appId)
const barometerUrl = computed(() =>
  getLink({
    page: 'barometer',
    slug: props.car.slug,
  }),
)

useCarSwipe(computed(() => props.auctionCarPagination))

const { showSpecialConditionNotificationDialog, doesAuctionMeetConditions }
  = useSpecialConditionNotification({ auctionId: props.car.auctionId })
</script>

<template>
  <div v-if="car" class="w-full px-3 xl:pl-0">
    <SectionsDetailsTop :car="car" :barometer-url="barometerUrl" />

    <div class="flex flex-wrap pt-4">
      <div class="mb-4 flex w-full justify-center lg:mb-0 lg:w-1/3">
        <div class="flex w-full justify-center md:w-1/2 lg:w-full">
          <ElementsCarTopPagination
            :auction-car-pagination="auctionCarPagination"
            :catalog-number="car.catalogNumber"
          />
        </div>
      </div>

      <div
        class="flex w-full flex-wrap justify-center gap-3 lg:w-2/3 lg:flex-nowrap lg:justify-end lg:gap-0"
      >
        <elements-button class="!w-auto !px-8 lg:mr-3" :url="barometerUrl">
          {{ $t('car-details.make-an-offer') }}
        </elements-button>

        <CurrentAuctionButton
          v-if="auction?.slug && auction.stage !== 'FINISHED'"
          :slug="auction.slug"
          class="!px-6 lg:mr-6"
        />

        <div class="hidden lg:inline-flex">
          <ElementsCarInputPagination
            :auction-car-pagination="auctionCarPagination"
            :catalog-number="car.catalogNumber"
            :auction-id="car.auctionId"
          />
        </div>
      </div>
    </div>

    <SectionsDetailsAppContent v-bind="props" :car="car" :details="details" />

    <LazySectionsAuctionSpecialConditionDialog
      v-if="doesAuctionMeetConditions"
      :auction-id="car.auctionId"
      :car-id="car.id"
      :active="showSpecialConditionNotificationDialog"
      @close="showSpecialConditionNotificationDialog = false"
    />
  </div>
</template>
